/**
 * ContentAutocomplete - Autocomplete component for selecting video content references
 * @module common/ContentAutocomplete
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLazyQuery } from '@apollo/client'
import { Autocomplete, TextField, Chip, CircularProgress, Box, Avatar } from '@mui/material'
import { SEARCH_VIDEO_CONTENT } from 'gui/components/content-detail/content-references/queries'
import type { ContentReference } from 'gui/components/content-detail/types'

interface ContentAutocompleteProps {
  /** Selected video content reference (single selection only) */
  value: ContentReference[]
  /** Called when selection changes */
  onChange: (value: ContentReference[]) => void
  /** Called when an item is selected from dropdown */
  onSelect?: (value: ContentReference) => void
  /** Label for the field */
  label: string
  /** Whether field is disabled */
  disabled: boolean
}

/**
 * ContentAutocomplete Component
 *
 * An autocomplete dropdown specifically for video content.
 * Features:
 * - Prefetch initial options on dropdown open
 * - Search with 300ms debounce
 * - Single-select only
 * - Shows keyart image, title, and published status (green/red circle)
 * - Limits results to 50 with message
 */
function ContentAutocomplete (props: ContentAutocompleteProps) {
  const { value = [], onChange, onSelect, label = 'Search video content', disabled = false } = props

  // Local state
  const [inputValue, setInputValue] = useState<string>('')
  const [options, setOptions] = useState<ContentReference[]>(value) // Initialize with selected values
  const [hasSearched, setHasSearched] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // GraphQL lazy query for searching video content
  const [searchVideoContent, { loading, data, error }] = useLazyQuery(SEARCH_VIDEO_CONTENT, {
    fetchPolicy: 'network-only', // Always fetch fresh results
  })

  // Handle GraphQL errors
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error('ContentAutocomplete GraphQL Error:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        fullError: error,
      })
    }
  }, [error])

  // Cleanup debounce timer on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    },
    [],
  )

  // Update options when data changes
  // Always include selected items in options to avoid MUI warnings
  useEffect(() => {
    if (data?.contentList?.content) {
      // Start with search results
      const searchResults = data.contentList.content

      // Merge with selected values - ensure selected items are always in options
      // Create a map by contentId to avoid duplicates
      const optionsMap = new Map<number, ContentReference>()

      // Add selected values first
      value.forEach((item) => {
        optionsMap.set(item.contentId, item)
      })

      // Add search results (won't overwrite selected items due to Map)
      searchResults.forEach(
        (
          item: {
            id: string
            contentId: number
            contentType: string
            title?: string | null
            published?: boolean | null
            titleGroupId?: string | null
            imageContentRelationships?: Array<{
              id: string
              type: string
              image?: {
                id: string
                url: string
              } | null
            } | null> | null
          } | null,
        ) => {
          if (item && !optionsMap.has(item.contentId)) {
            // Get the keyart no-text image URL
            const imageContentRelationships = item.imageContentRelationships || []
            const keyartNoTextImage = imageContentRelationships.find(
              (rel) => rel?.type === 'd6-keyart-no-text' && rel?.image?.url,
            )
            const thumbnailUrl = keyartNoTextImage?.image?.url || null

            // Transform search result to ContentReference format
            optionsMap.set(item.contentId, {
              id: item.id,
              contentId: item.contentId,
              contentType: item.contentType,
              title: item.title || '',
              published: item.published || false,
              titleGroupId: item.titleGroupId || '',
              referenceId: '', // Will be set when saved
              contentReferenceType: '', // Will be set when saved
              thumbnailUrl, // Add thumbnail URL for rendering
            })
          }
        },
      )

      setOptions(Array.from(optionsMap.values()))
    }
  }, [data, value])

  // Prefetch initial results when dropdown opens
  const handleOpen = useCallback(() => {
    setIsOpen(true)
    if (!hasSearched) {
      const variables: {
        title: string
        limit: number
        language: string[]
      } = {
        title: '%', // Match all
        limit: 50,
        language: ['en', '_primary'], // English first, then primary language fallback
      }

      searchVideoContent({ variables })
      setHasSearched(true)
    }
  }, [hasSearched, searchVideoContent])

  // Handle dropdown close
  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Handle input change with debounce
  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      // Clear input when user selects an option
      if (reason === 'reset') {
        setInputValue('')
        return
      }

      setInputValue(newInputValue)

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Debounce search for 300ms
      debounceTimerRef.current = setTimeout(() => {
        const searchTerm = newInputValue.trim()

        const variables: {
          title: string
          limit: number
          language: string[]
        } = {
          title: searchTerm ? `%${searchTerm}%` : '%', // Partial match
          limit: 50,
          language: ['en', '_primary'], // English first, then primary language fallback
        }

        // Search with partial match
        searchVideoContent({ variables })
      }, 300)
    },
    [searchVideoContent],
  )

  // Handle selection change (single selection only)
  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: ContentReference | null) => {
      // Handle single selection only
      if (newValue) {
        onChange([newValue])
        // Call onSelect callback when item is selected
        if (onSelect) {
          onSelect(newValue)
        }
      } else {
        onChange([])
      }
    },
    [onChange, onSelect],
  )

  // Get option label
  const getOptionLabel = useCallback((option: ContentReference) => option.title || '', [])

  // Check if two options are equal
  const isOptionEqualToValue = useCallback(
    (option: ContentReference, val: ContentReference) => option.contentId === val.contentId,
    [],
  )

  // Render option with image, title, and published status indicator
  const renderOption = useCallback(
    (optionProps: React.HTMLAttributes<HTMLLIElement>, option: ContentReference) => (
      <li {...optionProps} key={option.contentId}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Published status indicator */}
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: option.published ? '#4caf50' : '#f44336', // Green for published, red for unpublished
              flexShrink: 0,
            }}
          />
          {/* Thumbnail image */}
          <Avatar
            src={option.thumbnailUrl || undefined}
            variant="square"
            sx={{
              width: 40,
              height: 24,
              borderRadius: 1,
              bgcolor: option.thumbnailUrl ? 'transparent' : '#f5f5f5',
            }}
          >
            {!option.thumbnailUrl && <Box sx={{ fontSize: '10px', color: '#999' }}>No Image</Box>}
          </Avatar>
          {/* Title */}
          <Box sx={{ flex: 1, minWidth: 0 }}>{option.title}</Box>
        </Box>
      </li>
    ),
    [],
  )

  // Render selected tags (single selection, but keep the interface for consistency)
  const renderTags = useCallback(
    (tagValue: ContentReference[], getTagProps: (args: { index: number }) => object) =>
      tagValue.map((option, index) => (
        <Chip
          label={option.title}
          {...getTagProps({ index })}
          disabled={disabled}
          key={option.contentId}
        />
      )),
    [disabled],
  )

  // Filter options to hide already-selected items from dropdown
  // BUT keep them in options array for MUI validation
  const filterOptions = useCallback(
    (opts: ContentReference[]) => {
      // Get set of selected contentIds for quick lookup
      const selectedContentIds = new Set(value.map((item) => item.contentId))

      // Filter out already selected items from dropdown display
      return opts.filter((option) => !selectedContentIds.has(option.contentId))
    },
    [value],
  )

  // Show message if results are limited AND dropdown is open
  const helperText =
    isOpen && data?.contentList?.content?.length === 50
      ? 'Showing first 50 results. Type more to refine your search.'
      : ''

  return (
    <Autocomplete<ContentReference, false, undefined, undefined>
      multiple={false}
      value={value[0] || null}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onOpen={handleOpen}
      onClose={handleClose}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      renderTags={renderTags}
      loading={loading}
      disabled={disabled}
      filterOptions={filterOptions} // Hide selected items from dropdown
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default ContentAutocomplete
